<template>
  <div class="flex h-1 w-full flex-1">
    <div class="TVChartContainer flex-grow" :id="containerId" />
    <BaseSidebar v-if="!isWidget" position="right" size-class="w-[20rem]" v-model="open">
      <template #header>
        <div class="flex items-center space-x-2">
          <span>Favorite Pairs</span>
          <BaseButton v-if="open" size="xs" @click="showExchangeModal = true">+ Add Pair</BaseButton>
        </div>
      </template>
      <template #default>
        <div class="mb-1 overflow-y-auto" @dragover.prevent @drop.prevent="drop">
          <BaseDivider />
          <div v-if="pairsLoading || coinsLoading" class="flex h-98 w-full items-center justify-center">
            <BaseLoader />
          </div>
          <div v-else-if="userPairs.length > 0">
            <div
              v-for="(pair, index) in orderedPairs"
              :key="pair.id"
              :draggable="enableDragDrop ? true : false"
              @dragstart="dragIndex = index"
              @dragenter="dropIndex = index"
            >
              <div
                class="mx-2 mt-2 flex rounded-md p-2"
                :class="JSON.stringify(selectedFavoritePair) == JSON.stringify(pair) ? 'bg-blue-800' : 'bg-gray-800'"
              >
                <div class="flex flex-grow cursor-pointer items-center" @click="selectPair(pair)">
                  <IconMenu
                    @mouseover="enableDragDrop = true"
                    @mouseleave="enableDragDrop = false"
                    class="mr-1 flex h-3.5 w-3.5 flex-shrink-0 cursor-move text-gray-500"
                  ></IconMenu>
                  <div class="flex">
                    <div class="relative flex items-center">
                      <BaseCoinIcon :key="pair.base_symbol_uid" class="absolute h-4 w-4" :coin="pair.base_symbol_uid" />
                      <BaseCoinIcon :key="pair.quote_symbol_uid" class="ml-2 h-4 w-4" :coin="pair.quote_symbol_uid" />
                    </div>
                    <div class="my-auto flex flex-col">
                      <span>{{ pair.pair }} </span>
                      <span class="text-xs text-gray-400">{{ pair.name }} </span>
                    </div>
                  </div>
                  <div class="ml-auto">
                    <span class="flex max-w-[8rem] justify-end text-right">{{ displayVolume(pair) }} </span>
                    <BasePercentClassic
                      class="flex max-w-[8rem] justify-end"
                      :percent="pair.volume_change_24h"
                      :multiply="false"
                    />
                  </div>
                </div>
                <div class="ml-auto flex items-center pl-2">
                  <BaseButton type="ghost" size="xxs" @press="removePairFromFavorites(pair)">
                    <IconCross size="h-3 w-3 text-red-400" />
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex h-98 w-full items-center justify-center text-xs text-gray-400">No pairs found</div>
        </div>
      </template>
    </BaseSidebar>
    <ModalExchange
      :show="showExchangeModal"
      :exchanges="myExchanges"
      :all-exchanges="exchanges"
      :pairs="userPairs.map(x => x.id)"
      :favorites-modal="true"
      @close="showExchangeModal = false"
      @save="saveFavoritePairs($event)"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { widget } from '../../../../public/charting_library';
import { TheTieApi } from '@/components/tradingview/util/api';
import { TieDataFeed, getTIEIndicator } from '../../../../public/datafeeds/tie/src/tie-datafeed';
import ModalExchange from '@/components/modal/ModalExchange.vue';
import { getCurrentLocation, getStoredLocation } from '@/composeables/helpers.js';
import { useStore } from 'vuex';
import { formatPrice, formatNumber } from '@/composeables/filters';
import { useChartInitializer } from '@/composeables/chartInitializer.js';
import { useGetSidebarStatus } from '@/composeables/sidebar';
import ObjectToCSV from 'object-to-csv';
import useEmitter from '@/composeables/emitter';
import useHttp from '@/composeables/http';
import groupBy from 'lodash/groupBy';
import { CcxtApi } from '@/api/ccxtApi';

const emitter = useEmitter();
const store = useStore();
const $http = useHttp();
const $ccxt = new CcxtApi();
const { exchanges, init, myExchanges, getCategories, fetchWatchlists, watchlists } = useChartInitializer(store);

const emits = defineEmits(['reset:state']);
const props = defineProps({
  filters: Object,
  coinUid: {
    type: String,
    default: null
  },
  isWidget: {
    type: Boolean,
    default: false
  }
});

//WATCHLISTS
const coins = computed(() => store.getters.coins);
const coinsLoading = computed(() => store.getters.coinsLoader);
watch(coins, () => {
  if (coins.value.length > 0) {
    if (!tvWidget.value) initChart();
    userPairs.value = userPairs.value.map(x => {
      return {
        ...x,
        base_symbol_uid: coins.value.find(c => c.coin.ticker == x.base_symbol)?.coin_uid || '',
        quote_symbol_uid: coins.value.find(c => c.coin.ticker == x.quote_symbol)?.coin_uid || ''
      };
    });
  }
});
watch(watchlists, () => {
  if (datafeed.value) {
    datafeed.value.categories = getCategories();
  }
});

//DRAG AND DROP PAIRS
const enableDragDrop = ref(false);
const pairsOrder = ref([]);
const dragIndex = ref(null);
const dropIndex = ref(null);

const orderedPairs = computed(() => {
  const orderedPairs = [];
  pairsOrder.value.forEach(pairId => {
    const pair = userPairs.value.find(p => p.id === pairId);
    if (pair && pair.id) orderedPairs.push(pair);
  });
  return orderedPairs.concat(userPairs.value.filter(w => !pairsOrder.value.includes(w.id)));
});

onMounted(() => {
  try {
    pairsOrder.value = JSON.parse(window.localStorage.getItem('pairs_order')) || [];
  } catch {
    window.localStorage.setItem('pairs_order', '[]');
    pairsOrder.value = [];
  }
});

function drop() {
  const dragItem = orderedPairs.value[dragIndex.value];

  const remainingItems = orderedPairs.value.filter(item => item.id != dragItem.id);

  const reorderedItems = [
    ...remainingItems.slice(0, dropIndex.value),
    dragItem,
    ...remainingItems.slice(dropIndex.value)
  ];

  pairsOrder.value = reorderedItems.map(w => w.id);
  window.localStorage.setItem('pairs_order', JSON.stringify(pairsOrder.value));

  dragIndex.value = null;
  dropIndex.value = null;
}

//CHART INITIALIZATION
const api = new TheTieApi();
const containerId = `tv_chart_container_${Math.random()}`;
const symbol = 'CCXT:BTC/USDT:exchange:coinbasepro';
const tvWidget = ref(null);
const datafeed = ref(undefined);
const manageEventsShown = ref(true);
const coinTicker = computed(() => {
  if (props.coinUid && coins.value) return coins.value.find(x => x.coin_uid == props.coinUid)?.ticker || null;
  return null;
});
const selectedPair = props.isWidget
  ? ref(coinTicker.value || props.filters?.symbolInfo?.ticker)
  : ref(window.localStorage.getItem('selectedPair'));
const urlSymbol = computed(() => {
  const url = new URL(window.location.href);
  return url.searchParams.get('symbol');
});
const chartSymbol = computed(() => {
  const searched = urlSymbol.value;
  return searched || selectedPair.value || symbol;
});
watch(
  () => props.filters?.symbolInfo,
  () => {
    if (props.isWidget) {
      selectedPair.value = coinTicker.value || props.filters?.symbolInfo?.ticker;
    }
  }
);
watch(
  [
    () => props.filters?.includeTweets,
    () => props.filters?.includeUnlocks,
    () => props.filters?.unlocks?.threshold,
    () => props.filters?.unlocks?.thresholdMetric,
    () => props.filters?.unlocks?.entities,
    () => props.filters?.unlocks?.types,
    () => props.filters?.feed,
    () => props.filters?.newsType
  ],
  () => {
    resetDatafeed();
  }
);
watch(
  () => props.filters?.tradingViewState,
  () => {
    if (tvWidget.value) {
      if (props.filters?.tradingViewState) {
        tvWidget.value.load(props.filters?.tradingViewState);
        tvWidget.value.activeChart().resetData();
      } else {
        selectedPair.value = '';
        selectedFavoritePair.value = null;
        window.localStorage.setItem('selectedFavoritePair', '');
        initChart();
      }
    }
  }
);
watch(
  () => props.coinUid,
  value => {
    if (value && props.isWidget) {
      selectedPair.value = coinTicker.value;
      if (tvWidget.value) {
        tvWidget.value.chart().setSymbol(coinTicker.value);
        tvWidget.value.chart().setChartType(2);
      }
    }
  }
);
function resetDatafeed() {
  if (datafeed.value) {
    datafeed.value.includeTweets = props.filters?.includeTweets;
    datafeed.value.includeUnlocks = props.filters?.includeUnlocks;
    datafeed.value.unlocks.threshold = props.filters?.unlocks?.threshold;
    datafeed.value.unlocks.thresholdMetric = props.filters?.unlocks?.thresholdMetric;
    datafeed.value.unlocks.entities = props.filters?.unlocks?.entities;
    datafeed.value.unlocks.types = props.filters?.unlocks?.types;
    datafeed.value.newsFeedId =
      props.filters?.feed?.id === 'selected_coin' || props.filters?.feed?.id === 0
        ? undefined
        : props.filters?.feed?.id;
    datafeed.value.hideNews = props.filters?.feed?.id === 0;
    datafeed.value.tagId = props.filters?.newsType?.type === 'tag' ? props.filters?.newsType?.id : undefined;
    datafeed.value.groupId = props.filters?.newsType?.type === 'group' ? props.filters?.newsType?.id : undefined;
    if (tvWidget.value && tvWidget.value._ready) tvWidget.value.activeChart().resetData();
  }
}
function getLanguageFromURL() {
  const regex = new RegExp('[\\?&]lang=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
}
function createDownloadCsvButton() {
  const button = tvWidget.value.createButton();
  button.setAttribute('title', 'Exports chart data as a CSV');
  button.classList.add('apply-common-tooltip');
  button.addEventListener('click', () => {
    tvWidget.value
      .activeChart()
      .exportData()
      .then(({ data, schema }) => {
        const indicators = schema
          .slice(5)
          .map(r => (r.plotTitle === '-- output name --' ? r.sourceTitle : r.plotTitle));
        const csvData = [];
        data.forEach(row => {
          const csvRow = {};
          csvRow['Time'] = new Date(row[0] * 1000).toISOString(); // time
          csvRow['Price'] = row[1]; // price (open in tv)
          // add selected indicators
          for (let i = 5; i < row.length; i++) {
            const key = schema[i].plotTitle === '-- output name --' ? schema[i].sourceTitle : schema[i].plotTitle;
            csvRow[key] = row[i];
          }
          csvData.push(csvRow);
        });
        const newsColumns = ['News'];
        if (props.filters?.feed?.id != 0) {
          const marks = JSON.parse(window.localStorage.getItem('chartNewsMarks'));
          if (marks && marks.length > 0) {
            let lastRowNo = 0;
            marks.forEach(mark => {
              let inserted = false;
              for (let i = lastRowNo; i < csvData.length && !inserted; i++) {
                if (new Date(csvData[i].Time) >= new Date(mark.time)) {
                  lastRowNo = i;
                  let index = i == 0 || i == csvData.length - 1 ? i : i - 1;
                  if (!('NewsCount' in csvData[index])) csvData[index]['NewsCount'] = 0;
                  let x = csvData[index]['NewsCount'];
                  if (x == newsColumns.length) {
                    newsColumns.push('News ' + x);
                  }
                  csvData[index][newsColumns[x]] = mark.link;
                  inserted = true;
                  csvData[index]['NewsCount']++;
                }
              }
            });
          }
        }
        const otc = new ObjectToCSV({
          keys: ['Time', 'Price', ...indicators, ...newsColumns].map(key => ({ key, as: key })),
          data: csvData
        });
        const url = window.URL.createObjectURL(new Blob([otc.getCSV().replaceAll('undefined', '')]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'export.csv');
        document.body.appendChild(link);
        link.click();
      });
  });
  button.innerHTML = 'Export CSV';
}
function createManageEventsButton() {
  const button = tvWidget.value.createButton();
  button.setAttribute('title', 'Edit the type of events shown');
  button.classList.add('apply-common-tooltip');
  button.innerHTML = 'Hide Options';
  button.addEventListener('click', () => {
    manageEventsShown.value = !manageEventsShown.value;
    emitter.$emit('update:manageEventsShown', manageEventsShown.value);
    button.innerHTML = manageEventsShown.value ? 'Hide Options' : 'Show Options';
  });
}
function handleSymbolChange(symbol) {
  if (props.isWidget && symbol.name !== props.filters.symbolInfo.name) {
    selectedPair.value = symbol.ticker;
    emits('reset:state', { symbolInfo: symbol });
  }
  let pair = userPairs.value.find(x => x.pair == symbol.name && x.exchange == symbol.exchange);
  if (pair) {
    selectedFavoritePair.value = pair;
    selectedPair.value = symbol.ticker;
    window.localStorage.setItem('selectedPair', selectedPair.value);
    window.localStorage.setItem('selectedFavoritePair', JSON.stringify(selectedFavoritePair.value));
  }
  if (symbol.ticker !== selectedPair.value) {
    selectedFavoritePair.value = null;
    window.localStorage.setItem('selectedFavoritePair', '');
  }
}
function initChart() {
  let unlock = {
    threshold: props.filters?.unlocks?.threshold,
    thresholdMetric: props.filters?.unlocks?.thresholdMetric,
    entities: props.filters?.unlocks?.entities,
    types: props.filters?.unlocks?.types
  };
  datafeed.value = new TieDataFeed(coins.value, api, getCategories(), filteredExchanges.value, unlock);
  if (!props.filters?.newsType) {
    emitter.$emit('reset:newsType');
    datafeed.value.hideNews = true;
  }
  const widgetOptions = {
    symbol: chartSymbol.value,
    datafeed: datafeed.value,
    interval: props.filters?.interval || '5',
    saved_data: props.filters?.tradingViewState,
    container_id: containerId,
    library_path: '/charting_library/',
    locale: getLanguageFromURL() || 'en',
    disabled_features: ['use_localstorage_for_settings', 'header_saveload', 'save_shortcut'],
    enabled_features: ['study_templates'],
    charts_storage_url: 'https://saveload.tradingview.com',
    charts_storage_api_version: '1.1',
    client_id: 'tradingview.com',
    user_id: 'public_user_id',
    fullscreen: false,
    autosize: true,
    timezone: getStoredLocation() || getCurrentLocation(),
    theme: 'dark',
    custom_css_url: '/tradingview.css',
    /* todo figure out why this not not working, https://github.com/tradingview/charting_library/wiki/Widget-Constructor#loading_screen */
    loading_screen: { backgroundColor: '#0f172a' },
    toolbar_bg: '#0f172a',
    auto_save_delay: 1,
    overrides: {
      'mainSeriesProperties.style': 1,
      'paneProperties.background': 'rgb(15,23,42)',
      'platformProperties.background': '#FFFFFF'
    },
    studies_overrides: {
      'volume.volume.color.0': '#EF9A9A',
      'volume.volume.color.1': '#A5D6A7'
    },
    time_frames: [
      { text: '1y', resolution: '1D', description: '1 Year' },
      { text: '6m', resolution: '1D', description: '6 Months' },
      { text: '3m', resolution: '1D', description: '3 Months' },
      { text: '1m', resolution: '1D', description: '1 Month' },
      { text: '1d', resolution: '60', description: '1 Day' }
    ],
    custom_indicators_getter: function (PineJS) {
      return Promise.resolve([
        getTIEIndicator('daily_sentiment', PineJS),
        getTIEIndicator('tweet_volume', PineJS),
        getTIEIndicator('trading_volume', PineJS),
        getTIEIndicator('price_return_24_hours', PineJS),
        getTIEIndicator('price_return_1_hour', PineJS),
        getTIEIndicator('daily_sentiment_avg_30_days', PineJS),
        getTIEIndicator('daily_sentiment_change_7_days', PineJS),
        getTIEIndicator('daily_sentiment_momentum', PineJS),
        getTIEIndicator('hourly_sentiment', PineJS),
        getTIEIndicator('long_term_sentiment', PineJS),
        getTIEIndicator('market_cap', PineJS),
        getTIEIndicator('price_btc', PineJS),
        getTIEIndicator('price_return_30_days', PineJS),
        getTIEIndicator('price_return_btc_1_hour', PineJS),
        getTIEIndicator('trading_volume_30_day_avg', PineJS),
        getTIEIndicator('tweet_volume_30_day_avg', PineJS),
        getTIEIndicator('tweet_volume_dominance', PineJS),
        getTIEIndicator('trading_volume_dominance', PineJS),
        getTIEIndicator('price_return_btc_24_hours', PineJS),
        getTIEIndicator('price_return_btc_30_days', PineJS),
        getTIEIndicator('relative_trade_volume', PineJS),
        // getTIEIndicator('simple_moving_average_50_days', PineJS),
        // getTIEIndicator('simple_moving_average_change_50_days', PineJS),
        getTIEIndicator('hype_to_activity_ratio', PineJS),
        getTIEIndicator('nvtweet_ratio', PineJS),
        getTIEIndicator('relative_tweet_volume', PineJS),
        getTIEIndicator('tweet_unique_sources_percent', PineJS),
        getTIEIndicator('tweet_unique_sources_percent_30_day_avg', PineJS),
        getTIEIndicator('twitter_accounts', PineJS),
        getTIEIndicator('twitter_accounts_30_day_avg', PineJS),
        getTIEIndicator('tweet_volume_1_minute', PineJS),
        getTIEIndicator('tweet_unique_sources_percent_1_minute', PineJS),
        getTIEIndicator('news_stories_v2_1_day', PineJS),
        getTIEIndicator('news_stories_v2_7_day', PineJS),
        getTIEIndicator('news_stories_v2_30_day_avg', PineJS),
        getTIEIndicator('relative_news_v2', PineJS),
        getTIEIndicator('tvl', PineJS),
        getTIEIndicator('tvl_avalanche', PineJS),
        getTIEIndicator('tvl_ethereum', PineJS),
        getTIEIndicator('tvl_polygon', PineJS),
        getTIEIndicator('tvl_tezos', PineJS),
        getTIEIndicator('market_cap_dominance', PineJS),
        getTIEIndicator('youtube_view_count', PineJS),
        getTIEIndicator('youtube_view_count_30d_avg', PineJS),
        getTIEIndicator('tiktok_play_count', PineJS),
        getTIEIndicator('tiktok_play_count_30d_avg', PineJS),
        getTIEIndicator('fourchan_post_count', PineJS),
        getTIEIndicator('fourchan_post_count_30d_avg', PineJS),
        // avalanche
        getTIEIndicator('num_inc_24_avalanche', PineJS),
        getTIEIndicator('num_dec_24_avalanche', PineJS),
        getTIEIndicator('num_txs_24h_avalanche', PineJS),
        getTIEIndicator('num_active_addrs_24h_avalanche', PineJS),
        getTIEIndicator('num_inc_30d_avg_avalanche', PineJS),
        getTIEIndicator('num_dec_30d_avg_avalanche', PineJS),
        getTIEIndicator('num_tx_30d_avg_avalanche', PineJS),
        getTIEIndicator('num_active_addrs_30d_avg_avalanche', PineJS),
        getTIEIndicator('flow_pct_avalanche', PineJS),
        getTIEIndicator('supply_pct_avalanche', PineJS),
        // ethereum
        getTIEIndicator('num_inc_24_ethereum', PineJS),
        getTIEIndicator('num_dec_24_ethereum', PineJS),
        getTIEIndicator('num_txs_24h_ethereum', PineJS),
        getTIEIndicator('num_active_addrs_24h_ethereum', PineJS),
        getTIEIndicator('num_inc_30d_avg_ethereum', PineJS),
        getTIEIndicator('num_dec_30d_avg_ethereum', PineJS),
        getTIEIndicator('num_tx_30d_avg_ethereum', PineJS),
        getTIEIndicator('num_active_addrs_30d_avg_ethereum', PineJS),
        getTIEIndicator('flow_pct_ethereum', PineJS),
        getTIEIndicator('supply_pct_ethereum', PineJS),
        // polygon
        getTIEIndicator('num_inc_24_polygon', PineJS),
        getTIEIndicator('num_dec_24_polygon', PineJS),
        getTIEIndicator('num_txs_24h_polygon', PineJS),
        getTIEIndicator('num_active_addrs_24h_polygon', PineJS),
        getTIEIndicator('num_inc_30d_avg_polygon', PineJS),
        getTIEIndicator('num_dec_30d_avg_polygon', PineJS),
        getTIEIndicator('num_tx_30d_avg_polygon', PineJS),
        getTIEIndicator('num_active_addrs_30d_avg_polygon', PineJS),
        getTIEIndicator('flow_pct_polygon', PineJS),
        getTIEIndicator('supply_pct_polygon', PineJS),

        // tezos
        getTIEIndicator('num_inc_24_tezos', PineJS),
        getTIEIndicator('num_dec_24_tezos', PineJS),
        getTIEIndicator('num_txs_24h_tezos', PineJS),
        getTIEIndicator('num_active_addrs_24h_tezos', PineJS),
        getTIEIndicator('num_inc_30d_avg_tezos', PineJS),
        getTIEIndicator('num_dec_30d_avg_tezos', PineJS),
        getTIEIndicator('num_tx_30d_avg_tezos', PineJS),
        getTIEIndicator('num_active_addrs_30d_avg_tezos', PineJS),
        getTIEIndicator('flow_pct_tezos', PineJS),
        getTIEIndicator('supply_pct_tezos', PineJS),
        // getTIEIndicator('num_inc_24', PineJS),
        // getTIEIndicator('num_dec_24', PineJS),
        // getTIEIndicator('num_txs_24h', PineJS),
        // getTIEIndicator('num_active_addrs_24h', PineJS),
        // getTIEIndicator('num_inc_30d_avg_ethereum', PineJS),
        // getTIEIndicator('num_dec_30d_avg_ethereum', PineJS),
        // getTIEIndicator('num_tx_30d_avg', PineJS),
        // getTIEIndicator('num_active_addrs_30d_avg_ethereum', PineJS),
        getTIEIndicator('mcap_tvl_ethereum', PineJS),
        getTIEIndicator('mcap_tvl_avalanche', PineJS),
        getTIEIndicator('mcap_tvl_polygon', PineJS),
        getTIEIndicator('mcap_tvl_tezos', PineJS),

        getTIEIndicator('active_users_30d', PineJS),
        getTIEIndicator('commit_count', PineJS),
        getTIEIndicator('supply_in_circulation', PineJS),
        getTIEIndicator('diluted_valuation', PineJS),
        getTIEIndicator('beta', PineJS),
        getTIEIndicator('volatility_index', PineJS),
        getTIEIndicator('sharpe_ratio', PineJS),
        getTIEIndicator('circulating_supply', PineJS),
        getTIEIndicator('max_supply', PineJS),
        getTIEIndicator('total_supply', PineJS)
      ]);
    }
  };
  tvWidget.value = new widget(widgetOptions);
  const numberFormat = price => {
    return formatPrice(price).toLocaleString();
  };
  const formatChart = () => {
    const panes = tvWidget.value.activeChart().getPanes();
    panes.forEach(pane => {
      let paneFormatter = pane._pane.mainDataSource().formatter();
      if (paneFormatter.type == 'price') {
        paneFormatter.format = numberFormat;
      }
    });
  };
  tvWidget.value.onChartReady(() => {
    if (props.isWidget) {
      resetDatafeed();
      if (!props.filters?.tradingViewState && props.filters?.indicators) {
        tvWidget.value.activeChart().removeAllStudies();
        props.filters?.indicators.forEach(ind =>
          tvWidget.value.activeChart().createStudy(ind, ind == 'Volume' ? true : false)
        );
      }
      tvWidget.value
        .activeChart()
        .onIntervalChanged()
        .subscribe(null, (interval, timeframeObj) => {
          if (interval != props.filters.interval) emits('reset:state', { interval });
        });
    }
    tvWidget.value.subscribe('study_event', () => {
      if (props.isWidget) {
        const indicators = tvWidget.value
          .activeChart()
          .getAllStudies()
          .map(x => x.name);
        emits('reset:state', { indicators });
      }
      formatChart();
    });
    tvWidget.value
      .activeChart()
      .onSymbolChanged()
      .subscribe(null, symbol => {
        handleSymbolChange(symbol);
      });
    tvWidget.value.headerReady().then(() => {
      if (props.isWidget && coinTicker.value) {
        tvWidget.value.activeChart().setChartType(2);
        tvWidget.value.activeChart().setSymbol(coinTicker.value);
      }
      createDownloadCsvButton();
      createManageEventsButton();
    });
    emitter.$emit('set:layout');
    tvWidget.value.activeChart().priceFormatter().format = numberFormat;
    tvWidget.value.subscribe('onAutoSaveNeeded', () => {
      emitter.$emit('set:enableSaveBtn');
    });
    tvWidget.value.subscribe('tie', () => formatChart());
  });
}
onMounted(async () => {
  emitter.$on('reset:watchlists', async () => {
    await fetchWatchlists();
    initChart();
  });
  emitter.$on('save:TVWidget', saveCallback => {
    if (tvWidget.value) tvWidget.value.save(saveCallback);
  });
  await init();
  if (store.getters.coins.length > 0 && !tvWidget.value) {
    initChart();
  }
});
onUnmounted(() => {
  emitter.$off('save:TVWidget');
  if (tvWidget.value !== null) {
    tvWidget.value.remove();
    tvWidget.value = null;
    window.localStorage.removeItem('chartNewsMarks');
  }
});

//EXCHANGES
const { open } = useGetSidebarStatus('trading_view');
const defaultTimeframes = {
  '1m': '1',
  '5m': '5',
  '30m': '30',
  '1h': '60',
  '4h': '240',
  '6h': '360',
  '1d': 'D',
  '1w': 'W',
  '1M': 'M'
};
const showExchangeModal = ref(false);
const filteredExchanges = computed(() => {
  return exchanges.value.map(i => {
    let timeframes = i.timeframes?.split(',') || [];
    let resolveFrames = [];
    timeframes.forEach(item => {
      if (defaultTimeframes[item]) resolveFrames.push(defaultTimeframes[item]);
    });
    return {
      exchange: i.exchange,
      timeframes: resolveFrames
    };
  });
});

//EXCHANGE PAIRS
const selectedFavoritePair = ref(null);
const userPairs = ref([]);
const pairsLoading = ref(true);
onMounted(async () => {
  selectedFavoritePair.value = window.localStorage.getItem('selectedFavoritePair')
    ? JSON.parse(window.localStorage.getItem('selectedFavoritePair'))
    : null;
  await getFavoritePairs();
});

async function getExchangePairs(pairs) {
  const response = await $ccxt.getExchangePairs({
    ids: pairs
  });
  const allExchanges = groupBy(exchanges.value, 'exchange');
  userPairs.value = response.data.data;
  userPairs.value = userPairs.value.map(x => {
    const exchange = allExchanges[x.exchange][0] || null;
    return {
      ...x,
      name: exchange?.name || x.exchange,
      base_symbol_uid:
        coins.value.find(c => c.coin.ticker.toLowerCase() == x.base_symbol.toLowerCase())?.coin_uid || '',
      quote_symbol_uid:
        coins.value.find(c => c.coin.ticker.toLowerCase() == x.quote_symbol.toLowerCase())?.coin_uid || ''
    };
  });
  pairsLoading.value = false;
}
async function saveFavoritePairs(pairs) {
  userPairs.value = pairs;
  await $http.post('/favorite_pairs', { pairs: userPairs.value.map(x => x.id) });
}
async function getFavoritePairs() {
  let pairIds = [];
  await $http.get('/favorite_pairs').then(res => {
    pairIds = res.data;
  });
  if (pairIds.length > 0) {
    getExchangePairs(pairIds);
  } else pairsLoading.value = false;
}
function selectPair(pair) {
  selectedPair.value = `CCXT:${pair.pair}:exchange:${pair.exchange}`;
  selectedFavoritePair.value = pair;
  window.localStorage.setItem('selectedPair', selectedPair.value);
  window.localStorage.setItem('selectedFavoritePair', JSON.stringify(selectedFavoritePair.value));
  tvWidget.value.activeChart().setSymbol(selectedPair.value);
}
async function removePairFromFavorites(pair) {
  userPairs.value = userPairs.value.filter(x => x.id != pair.id);
  await $http.post('/favorite_pairs', { pairs: userPairs.value.map(x => x.id) });
  if (selectedFavoritePair.value == pair) {
    selectedFavoritePair.value = null;
    selectedPair.value = '';
    window.localStorage.setItem('selectedPair', '');
    window.localStorage.setItem('selectedFavoritePair', '');
    tvWidget.value.chart().setSymbol('CCXT:BTC/USDT:exchange:coinbasepro');
  }
}
function displayVolume(pair) {
  if (pair.day_volume || pair.quote_volume) {
    if (pair.day_volume != 0 || pair.quote_volume != 0) {
      return (
        (pair.day_volume ? formatNumber(pair.day_volume, 2, '') : '-') +
        ' / ' +
        (pair.quote_volume ? formatNumber(pair.quote_volume, 2, '') : '-')
      );
    }
  }
  return '-';
}

//SETTING TIMEZONE
async function getTimeZoneFromChart() {
  return new Promise(resolve => {
    if (tvWidget.value && tvWidget.value._ready) {
      tvWidget.value?.save(async state => {
        resolve(state?.charts[0]?.timezone);
      });
    } else {
      resolve('');
    }
  });
}
onMounted(() => {
  setInterval(async () => {
    const timezone = await getTimeZoneFromChart();
    if (timezone) {
      if (timezone === getCurrentLocation()) window.localStorage.removeItem('tr_tie_location');
      else window.localStorage.setItem('tr_tie_location', timezone);
    }
  }, 1000);
});
</script>
